import { Dictionary } from '../../../types/globals';

export type MapCurried<T> = (fn: (value: T, index: number, array: Array<T>) => T) => (array: Array<T>) => Array<T>;
export type PluckCurried<K extends keyof T, T> = (prop: K) => (list: T[]) => Array<T[K]>;
export type ReduceCurried<T, TResult> = (
    fn: (acc: T | TResult, value: T, index: number, array: Array<T>) => TResult,
    acc?: TResult
) => (array: Array<T>) => TResult;

export const map: MapCurried<any> = fn => list => list.map(fn);
export const pipe = (...fns: Array<Function>) => fns.reduce((first, second) => (...args: any) => second(first(args)));
export const pluck: PluckCurried<string, Dictionary<any>> = prop => list => list.map(obj => obj[prop]);
export const reduce: ReduceCurried<any, any> = (fn, initial) => list => list.reduce(fn, initial);

// A pure reverse functon that does not change the array
export const reverse = <T>(arr: Array<T>) => [...arr].reverse();

/**
 * Creates a new list out of the two supplied by pairing up equally-positioned
 * items from both lists. The returned list is truncated to the length of the
 * shorter of the two input lists.
 * Note: ripped form https://github.com/ramda/ramda/blob/v0.27.0/source/zip.js
 * R.zip([1, 2, 3], ['a', 'b', 'c']); //=> [[1, 'a'], [2, 'b'], [3, 'c']]
 * R.zip([a, b, c], [d, e, f]) = [[a, d], [b, e], [c, f]]
 */
export const zip = (a: Array<any>, b: Array<any>): Array<any> => {
    const rv = [];
    let idx = 0;
    const len = Math.min(a.length, b.length);
    while (idx < len) {
        rv[idx] = [a[idx], b[idx]];
        idx += 1;
    }
    return rv;
};

/**
 * Returns a list of numbers from `from` (inclusive) to `to` (exclusive).
 * ripped from: https://github.com/ramda/ramda/blob/v0.27.0/source/range.js
 * R.range(1, 5);    //=> [1, 2, 3, 4]
 * R.range(50, 53);  //=> [50, 51, 52]
 */
export const range = (from: number, to: number) => {
    const result = [];
    let n = from;
    while (n < to) {
        result.push(n);
        n += 1;
    }
    return result;
};
